// OutsourcePrintWorkTicketSticker.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import JsBarcode from "jsbarcode";
import { Spinner } from "react-bootstrap";

const OutsourcePrintWorkTicketSticker = () => {
  const { caseNumber } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const barcodeRef = useRef(null);

  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

  // Helper function to get user info from localStorage
  const getUserInfo = () => {
    const userLabId = localStorage.getItem('userLabId') || localStorage.getItem('labId') || '';
    const userName = localStorage.getItem('userName') || localStorage.getItem('username') || 'Unknown';
    const labName = localStorage.getItem('labName') || localStorage.getItem('currentLabName') || '';
    const userId = localStorage.getItem('userId') || '';
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
    
    return { userLabId, userName, labName, userId, token };
  };

  // Fetch outsource case data according to caseNumber
  useEffect(() => {
    if (!caseNumber) return;

    const fetchOutsourceCaseDetails = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const { token, userLabId } = getUserInfo();

        const response = await fetch(
          `${API_BASE_URL}/api/outsource-case/case-number/${caseNumber}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'userLabId': userLabId,
              'x-user-lab-id': userLabId
            },
            withCredentials: true
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result && result.success) {
          setCaseData(result.data);
        } else {
          setError(result.message || 'Case not found');
        }
      } catch (err) {
        console.error("API Error:", err);
        setError(err.message || 'Failed to fetch case details');
      } finally {
        setLoading(false);
      }
    };

    fetchOutsourceCaseDetails();
  }, [caseNumber]);

  // Generate barcode and auto-print after caseData is loaded
  useEffect(() => {
    if (caseData && barcodeRef.current) {
      // Clear any existing barcode
      while (barcodeRef.current.firstChild) {
        barcodeRef.current.removeChild(barcodeRef.current.firstChild);
      }
      
      JsBarcode(barcodeRef.current, caseData.caseNumber || caseData.case_id, {
        format: "CODE128",
        displayValue: false,
        height: 40,
        width: 2,
        margin: 0,
        background: "#ffffff",
        lineColor: "#000000",
      });
      
      // Center the barcode SVG
      const svg = barcodeRef.current.querySelector('svg');
      if (svg) {
        svg.setAttribute('style', 'display: block; width: 100%; height: auto; max-height: 45px;');
      }

      // Small delay to ensure barcode renders properly before printing
      setTimeout(() => {
        window.print();
      }, 100);
    }
  }, [caseData]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <Spinner animation="border" style={{ color: '#004aad' }} />
        <p style={{ color: '#666', fontSize: '16px' }}>Loading outsource case details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ 
          color: '#721c24', 
          backgroundColor: '#f8d7da', 
          padding: '20px', 
          borderRadius: '5px',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <p style={{ margin: 0, fontSize: '16px' }}>Error: {error}</p>
        </div>
        <button
          onClick={() => window.location.href = '/outsource-received'}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <p>No case data found</p>
        <button
          onClick={() => window.location.href = '/outsource-received'}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Get the case number for display
  const displayCaseNumber = caseData.caseNumber || caseData.case_id || 'N/A';
  
  // Get provider name - try multiple possible fields
  const providerName = caseData.providerName || 
                       caseData.provider_name || 
                       caseData.sourceLab?.labName || 
                       caseData.createdBy?.labName || 
                       'N/A';
  
  // Get source lab name
  const sourceLabName = caseData.sourceLab?.labName || 
                        caseData.createdBy?.labName || 
                        'N/A';
  
  // Get creation date
  const creationDate = caseData.createdAt || caseData.date || new Date().toISOString();

  return (
    <div className="wrapper">
      {/* Buttons (hidden when printing) */}
      <div className="no-print button-group">
        <button onClick={() => window.print()}>Print Again</button>
        <button
          onClick={() => (window.location.href = `/outsource-case/${displayCaseNumber}`)}
        >
          Back to Case #{displayCaseNumber}
        </button>
      </div>

      {/* Label with case details */}
      <div className="label">
        {/* Content wrapper that pushes barcode to bottom */}
        <div className="content-wrapper">
          {/* Case number at top - bold and left aligned */}
          <div className="case-number">
            {displayCaseNumber}
          </div>
          
          {/* Provider name below case number - bold */}
          <div className="provider-name">{providerName}</div>
          
          {/* Source Lab name */}
          <div className="source-lab">{sourceLabName}</div>
          
          {/* Date */}
          <div className="date">
            {new Date(creationDate).toLocaleDateString("en-GB")}
          </div>
        </div>
        
        {/* Barcode at the very bottom */}
        <div className="barcode-container">
          <svg ref={barcodeRef} className="barcode-svg"></svg>
        </div>
      </div>

      {/* Styles - Fixed barcode positioning */}
      <style>{`
        * {
          box-sizing: border-box;
        }
        
        html, body, #root, .App, .wrapper {
          margin: 0 !important;
          padding: 0 !important;
          font-family: Arial, sans-serif;
          width: 100%;
          height: 100%;
        }
        
        .wrapper {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;
          min-height: 100vh;
        }
        
        .button-group {
          display: flex;
          gap: 8px;
          margin: 10px !important;
          padding: 0 !important;
        }
        
        .button-group button {
          background-color: #2b82d9;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 13px;
          cursor: pointer;
        }
        
        .button-group button:hover {
          background-color: #1f6bb8;
        }
        
   .label {
  margin: 0 !important;
  padding: 2mm 1.2mm 1mm 1.2mm;   /* was 2mm 2mm 1mm 2mm */
  border: none;
  width: 52.07mm;
  height: 38.1mm;
  page-break-after: avoid;
  page-break-inside: avoid;
  break-inside: avoid;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
        .content-wrapper {
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .case-number {
          font-weight: 900;
          font-size: 18px;
          text-align: left;
          margin: 0;
          padding: 0;
          line-height: 1.2;
          letter-spacing: 0.5px;
          text-shadow: 0.5px 0 0 currentColor;
        }

        .provider-name {
          font-size: 14px;
          font-weight: 900;
          text-align: left;
          margin: 3px 0 0 0;
          padding: 0;
          line-height: 1.2;
          letter-spacing: 0.3px;
          text-shadow: 0.3px 0 0 currentColor;
        }

        .source-lab {
          font-size: 13px;
          font-weight: 700;
          text-align: left;
          margin: 2px 0 0 0;
          padding: 0;
          line-height: 1.2;
          letter-spacing: 0.3px;
          color: #004aad;
        }

        .date {
          font-weight: 800;
          font-size: 13px;
          text-align: left;
          margin: 3px 0 0 0;
          padding: 0;
          line-height: 1.2;
          letter-spacing: 0.3px;
          text-shadow: 0.3px 0 0 currentColor;
        }

   .barcode-container {
  display: flex;
  justify-content: flex-start;   /* instead of center */
  align-items: center;
  width: 100%;
  height: 50px;
  flex-shrink: 0;
  padding: 0;
  margin: 0;
  background: white;
  margin-left: -2mm;   /* adjust between -1.5mm and -2mm if needed */
}
        
        .barcode-svg {
          width: 100%;
          height: auto;
          max-height: 45px;
          display: block;
        }
        
        @media print {
          @page {
            size: 52.07mm 38.1mm;
            margin: 0;
            padding: 0;
          }
          
          body, html {
            margin: 0 !important;
            padding: 0 !important;
            width: 52.07mm;
            height: 38.1mm;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .no-print {
            display: none !important;
          }
          
          .wrapper {
            width: 52.07mm;
            height: 38.1mm;
            display: block;
            margin: 0 !important;
            padding: 0 !important;
            min-height: auto;
          }
          
          .label {
            margin: 0 !important;
            padding: 2mm 2mm 1mm 2mm;
            border: none;
            width: 52.07mm;
            height: 38.1mm;
            page-break-after: avoid;
            page-break-inside: avoid;
            break-inside: avoid;
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
          
          .case-number {
            font-weight: 900;
            font-size: 17px;
            margin: 0;
            padding: 0;
            line-height: 1.2;
            letter-spacing: 0.5px;
            text-shadow: 0.5px 0 0 currentColor;
          }
          
          .provider-name {
            font-weight: 900;
            font-size: 14px;
            margin: 3px 0 0 0;
            padding: 0;
            line-height: 1.2;
            letter-spacing: 0.3px;
            text-shadow: 0.3px 0 0 currentColor;
          }

          .source-lab {
            font-weight: 700;
            font-size: 13px;
            margin: 2px 0 0 0;
            padding: 0;
            line-height: 1.2;
            letter-spacing: 0.3px;
            color: #004aad;
          }
          
          .date {
            font-weight: 800;
            font-size: 13px;
            margin: 3px 0 0 0;
            padding: 0;
            line-height: 1.2;
            letter-spacing: 0.3px;
            text-shadow: 0.3px 0 0 currentColor;
          }
          
       .barcode-container {
  display: flex !important;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 50px;
  flex-shrink: 0;
  padding: 0;
  margin: 0;
  margin-left: -2mm;
  background: white;
}
          
          .barcode-svg {
            width: 100%;
            height: auto;
            max-height: 45px;
            display: block !important;
          }
          .content-wrapper,
.barcode-container {
    margin-left: -0.4mm;
}
          /* Ensure barcode SVG renders properly */
          .barcode-svg svg {
            display: block !important;
            width: 100% !important;
            height: auto !important;
            max-height: 45px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OutsourcePrintWorkTicketSticker;